import { useCallback, useState } from "react";
import {
  applyArticleImport,
  downloadArticleImportTemplate,
  previewArticleImport,
  type ArticleImportPreview,
} from "@/lib/articleExcel";
import { formatApiError } from "@/lib/formatApiError";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Table } from "@/components/ui/Table";

const ACTION_LABELS: Record<string, string> = {
  create: "Neu",
  update: "Aktualisieren",
  error: "Fehler",
};

export interface ArticleImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

export function ArticleImportModal({ open, onClose, onImported }: ArticleImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ArticleImportPreview | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setFile(null);
    setPreview(null);
    setError(null);
    setSuccess(null);
  }, []);

  function handleClose() {
    resetState();
    onClose();
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setPreview(null);
    setError(null);
    setSuccess(null);
    e.target.value = "";
  }

  async function onDownloadTemplate() {
    setLoadingTemplate(true);
    setError(null);
    try {
      await downloadArticleImportTemplate();
    } catch (err) {
      setError(formatApiError(err, "Vorlage konnte nicht heruntergeladen werden"));
    } finally {
      setLoadingTemplate(false);
    }
  }

  async function onPreview() {
    if (!file) {
      setError("Bitte eine Excel-Datei auswählen.");
      return;
    }
    setLoadingPreview(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await previewArticleImport(file);
      setPreview(result);
    } catch (err) {
      setError(formatApiError(err, "Vorschau fehlgeschlagen"));
    } finally {
      setLoadingPreview(false);
    }
  }

  async function onApply() {
    if (!file || !preview || preview.error_count > 0) return;
    setApplying(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await applyArticleImport(file);
      const parts = [
        result.created > 0 ? `${result.created} neu` : null,
        result.updated > 0 ? `${result.updated} aktualisiert` : null,
      ].filter(Boolean);
      const summary = parts.length > 0 ? parts.join(", ") : "Keine Änderungen";
      setSuccess(`${summary} importiert.`);
      if (result.errors.length > 0) {
        setError(result.errors.join(" "));
      } else {
        onImported();
      }
    } catch (err) {
      setError(formatApiError(err, "Import fehlgeschlagen"));
    } finally {
      setApplying(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Artikel importieren"
      description="Laden Sie die Excel-Vorlage herunter, füllen Sie die Artikel aus und importieren Sie die Datei."
      size="xl"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Schließen
          </Button>
          <Button
            type="button"
            loading={applying}
            disabled={!preview || preview.error_count > 0 || preview.valid_count === 0}
            onClick={() => void onApply()}
          >
            Importieren
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <Button
            type="button"
            variant="secondary"
            loading={loadingTemplate}
            onClick={() => void onDownloadTemplate()}
          >
            Excel-Vorlage herunterladen
          </Button>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Excel-Datei (.xlsx)</span>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onFileChange}
              className="block text-sm"
            />
          </label>
          <Button
            type="button"
            variant="secondary"
            loading={loadingPreview}
            disabled={!file}
            onClick={() => void onPreview()}
          >
            Vorschau
          </Button>
        </div>

        {file ? (
          <p className="text-sm text-label-secondary">
            Ausgewählt: <span className="font-medium text-label-primary">{file.name}</span>
          </p>
        ) : null}

        {preview ? (
          <div className="space-y-2">
            <p className="text-sm text-label-secondary">
              {preview.create_count} neu, {preview.update_count} Aktualisierung(en)
              {preview.error_count > 0 ? `, ${preview.error_count} Fehler` : ""}
            </p>
            <Table
              wide
              emptyMessage="Keine Zeilen."
              isEmpty={preview.rows.length === 0}
              className="max-h-80 overflow-y-auto"
            >
              <thead>
                <tr>
                  <th>Zeile</th>
                  <th>Aktion</th>
                  <th>Nr.</th>
                  <th>Bezeichnung</th>
                  <th>Hinweise</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={row.row_number}>
                    <td className="tabular-nums">{row.row_number}</td>
                    <td>
                      <span
                        className={
                          row.action === "error"
                            ? "text-status-red"
                            : row.action === "create"
                              ? "text-status-green"
                              : "text-label-secondary"
                        }
                      >
                        {ACTION_LABELS[row.action] ?? row.action}
                      </span>
                    </td>
                    <td className="font-mono text-sm">{row.article_number || "—"}</td>
                    <td>{row.name || "—"}</td>
                    <td className="text-sm text-label-secondary">
                      {[...row.errors, ...row.warnings].join(" ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}

        {success ? <p className="text-sm text-status-green">{success}</p> : null}
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
      </div>
    </Modal>
  );
}
